/**
 * ResetPassword — Send password-reset email
 */

import { useState, useContext } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import KDInput from "components/KDInput";
import KDAlert from "components/KDAlert";

import { AuthContext } from "context/AuthContext";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";

function ResetPassword() {
  const { resetPassword } = useContext(AuthContext);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.message || t("error.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <KDBox
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: darkMode
          ? "linear-gradient(195deg, #0F1114 0%, #161B1F 100%)"
          : "linear-gradient(195deg, #F8FAFC 0%, #F0FDFA 100%)",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420, p: 4, borderRadius: 3 }}>
        <KDBox textAlign="center" mb={3}>
          <KDTypography variant="h4" fontWeight="bold">
            {t("auth.resetPasswordTitle")}
          </KDTypography>
          <KDTypography variant="body2" color="text" mt={1}>
            {t("auth.resetPasswordSubtitle")}
          </KDTypography>
        </KDBox>

        {error && <KDAlert color="error" sx={{ mb: 2 }}>{error}</KDAlert>}

        {sent ? (
          <KDAlert color="success">
            {t("auth.resetPasswordSent")}
          </KDAlert>
        ) : (
          <KDBox component="form" onSubmit={handleSubmit}>
            <KDBox mb={2}>
              <KDInput
                label={t("auth.email")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </KDBox>
            <KDButton type="submit" variant="gradient" color="primary" fullWidth disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : t("auth.sendResetLink")}
            </KDButton>
          </KDBox>
        )}

        <KDBox mt={3} textAlign="center">
          <KDTypography variant="caption" color="text">
            <Link to="/login" style={{ color: "inherit" }}>
              {t("auth.backToLogin")}
            </Link>
          </KDTypography>
        </KDBox>
      </Card>
    </KDBox>
  );
}

export default ResetPassword;
